import { useRef, useState, useCallback } from "react";
import { MoveHorizontal } from "lucide-react";

// Draggable before/after comparison slider
export function BeforeAfter({ before, after, alt = "Before and after", testid }) {
  const ref = useRef(null);
  const [pos, setPos] = useState(50);
  const dragging = useRef(false);

  const update = useCallback((clientX) => {
    const el = ref.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const pct = ((clientX - rect.left) / rect.width) * 100;
    setPos(Math.min(100, Math.max(0, pct)));
  }, []);

  const onDown = (e) => {
    dragging.current = true;
    update(e.touches ? e.touches[0].clientX : e.clientX);
  };
  const onMove = (e) => {
    if (!dragging.current) return;
    update(e.touches ? e.touches[0].clientX : e.clientX);
  };
  const onUp = () => { dragging.current = false; };

  return (
    <div
      ref={ref}
      data-testid={testid}
      className="relative aspect-[4/3] overflow-hidden select-none cursor-ew-resize border border-black/10 bg-stone"
      onMouseDown={onDown}
      onMouseMove={onMove}
      onMouseUp={onUp}
      onMouseLeave={onUp}
      onTouchStart={onDown}
      onTouchMove={onMove}
      onTouchEnd={onUp}
      role="slider"
      aria-label="Before and after comparison slider"
      aria-valuenow={Math.round(pos)}
    >
      {/* After (base layer) */}
      <img src={after} alt={`${alt} — after`} className="absolute inset-0 w-full h-full object-cover" draggable={false} />
      {/* Before (clipped overlay) */}
      <div className="absolute inset-0 overflow-hidden" style={{ width: `${pos}%` }}>
        <img
          src={before}
          alt={`${alt} — before`}
          className="absolute inset-0 h-full object-cover"
          style={{ width: ref.current ? ref.current.offsetWidth : "100%" }}
          draggable={false}
        />
      </div>
      {/* Divider handle */}
      <div className="absolute top-0 bottom-0 w-0.5 bg-white shadow-[0_0_12px_rgba(0,0,0,0.4)]" style={{ left: `${pos}%` }}>
        <div className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 w-10 h-10 bg-white rounded-full shadow-lg flex items-center justify-center text-graphite">
          <MoveHorizontal size={16} />
        </div>
      </div>
      {/* Labels */}
      <span className="absolute top-3 left-3 bg-black/60 text-white font-mono text-[9px] tracking-[0.2em] uppercase px-2.5 py-1">Before</span>
      <span className="absolute top-3 right-3 bg-ember text-white font-mono text-[9px] tracking-[0.2em] uppercase px-2.5 py-1">After</span>
    </div>
  );
}
